import React, { useEffect, useState } from "react";
import { api, formatError } from "../../lib/api";
import { fileUrl } from "../../lib/seo";
import { Button } from "../../components/ui/button";
import { toast } from "sonner";
import { Download } from "lucide-react";

const pill = (s) => ({ running: "bg-green-100 text-green-800", completed: "bg-green-100 text-green-800", verified: "bg-blue-100 text-blue-800",
  pending: "bg-amber-100 text-amber-800", paused: "bg-gray-100 text-gray-700" }[s] || "bg-red-50 text-red-800");
const Th = ({ cols }) => <thead className="bg-[#4A0E17] text-[#F3E5AB]"><tr>{cols.map((c) => <th key={c} className="text-left px-4 py-3">{c}</th>)}</tr></thead>;
const Btn = (p) => <Button {...p} className={`h-8 text-xs rounded-full px-3 ${p.className || "bg-[#2F7D32] hover:bg-[#1B5E20]"}`} />;

export function AdsAdminTab() {
  const [items, setItems] = useState([]);
  const load = () => api.get("/admin/ads").then((r) => setItems(r.data.items));
  useEffect(() => { load(); }, []);
  const set = async (id, status) => { try { await api.put(`/admin/ads/${id}/status`, { status }); toast.success(status); load(); } catch (e) { toast.error(formatError(e)); } };
  return (
    <div data-testid="ads-admin-tab" className="bg-[#FFFDF9] sacred-border rounded-2xl overflow-x-auto">
      <table className="w-full text-sm min-w-[800px]">
        <Th cols={["Ad", "Advertiser", "Budget / spent", "Impr. / clicks", "Status", "Actions"]} />
        <tbody>
          {items.length === 0 && <tr><td colSpan={6} className="px-4 py-10 text-center text-[#63534E]">No ads yet.</td></tr>}
          {items.map((a) => (
            <tr key={a.id} className="border-t border-[#D4AF37]/20">
              <td className="px-4 py-3"><div className="flex gap-3 items-center"><img src={a.image_url} alt="" className="w-12 h-12 rounded object-cover" /><div><div className="font-semibold">{a.headline}</div><div className="text-xs text-[#63534E] line-clamp-1">{a.body}</div></div></div></td>
              <td className="px-4 py-3">{a.advertiser_name}<div className="text-xs text-[#63534E] font-mono">{a.advertiser_email}</div></td>
              <td className="px-4 py-3 font-mono">${a.budget_usd} / ${(a.spent_usd || 0).toFixed(3)}</td>
              <td className="px-4 py-3 font-mono">{a.impressions} / {a.clicks}</td>
              <td className="px-4 py-3"><span className={`px-2 py-0.5 rounded-full text-xs ${pill(a.status)}`}>{a.status}</span></td>
              <td className="px-4 py-3"><div className="flex gap-2">
                {a.status !== "running" && <Btn data-testid={`ad-run-${a.id}`} onClick={() => set(a.id, "running")}>{a.status === "pending" ? "Approve" : "Resume"}</Btn>}
                {a.status === "running" && <Btn data-testid={`ad-pause-${a.id}`} className="bg-[#8C6D3B] hover:bg-[#6d5430]" onClick={() => set(a.id, "paused")}>Pause</Btn>}
                {a.status === "pending" && <Btn data-testid={`ad-reject-${a.id}`} className="bg-red-700 hover:bg-red-800" onClick={() => set(a.id, "rejected")}>Reject</Btn>}
              </div></td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

async function openPrivate(path) {
  const r = await api.get(`/admin/private-files/${path}`, { responseType: "blob" });
  window.open(URL.createObjectURL(r.data), "_blank");
}

export function VerificationsAdminTab() {
  const [items, setItems] = useState([]);
  const load = () => api.get("/admin/verifications").then((r) => setItems(r.data.items));
  useEffect(() => { load(); }, []);
  const set = async (id, status) => {
    const note = status === "rejected" ? window.prompt("Reason (shown to teacher)") : null;
    try { await api.put(`/admin/gurus/${id}/verification`, { status, note }); toast.success(status); load(); } catch (e) { toast.error(formatError(e)); }
  };
  return (
    <div data-testid="verifications-admin-tab" className="bg-[#FFFDF9] sacred-border rounded-2xl overflow-x-auto">
      <table className="w-full text-sm min-w-[700px]">
        <Th cols={["Teacher", "Documents", "Submitted", "Status", "Actions"]} />
        <tbody>
          {items.length === 0 && <tr><td colSpan={5} className="px-4 py-10 text-center text-[#63534E]">No verification requests yet.</td></tr>}
          {items.map((g) => (
            <tr key={g.id} className="border-t border-[#D4AF37]/20">
              <td className="px-4 py-3"><div className="font-semibold">{g.name}</div><div className="text-xs text-[#63534E]">{g.title}</div></td>
              <td className="px-4 py-3"><div className="flex gap-3">
                <button data-testid={`view-id-${g.id}`} onClick={() => openPrivate(g.verification.id_path)} className="text-[#E65100] underline text-xs">ID</button>
                <button data-testid={`view-cert-${g.id}`} onClick={() => openPrivate(g.verification.cert_path)} className="text-[#E65100] underline text-xs">Certificate</button>
              </div></td>
              <td className="px-4 py-3 font-mono text-xs">{new Date(g.verification.submitted_at).toLocaleString()}</td>
              <td className="px-4 py-3"><span className={`px-2 py-0.5 rounded-full text-xs ${pill(g.verification.status)}`}>{g.verification.status}</span></td>
              <td className="px-4 py-3"><div className="flex gap-2">
                {g.verification.status !== "verified" && <Btn data-testid={`verify-approve-${g.id}`} className="bg-[#1D6FE0] hover:bg-[#1558B5]" onClick={() => set(g.id, "verified")}>Verify</Btn>}
                {g.verification.status === "pending" && <Btn data-testid={`verify-reject-${g.id}`} className="bg-red-700 hover:bg-red-800" onClick={() => set(g.id, "rejected")}>Reject</Btn>}
              </div></td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export function RedemptionsAdminTab() {
  const [items, setItems] = useState([]);
  const load = () => api.get("/admin/redemptions").then((r) => setItems(r.data.items));
  useEffect(() => { load(); }, []);
  const set = async (id, status) => { try { await api.put(`/admin/redemptions/${id}`, { status }); toast.success(status); load(); } catch (e) { toast.error(formatError(e)); } };
  return (
    <div data-testid="redemptions-admin-tab" className="bg-[#FFFDF9] sacred-border rounded-2xl overflow-x-auto">
      <table className="w-full text-sm min-w-[760px]">
        <Th cols={["Devotee", "Type", "Amount", "Details", "Status", "Actions"]} />
        <tbody>
          {items.length === 0 && <tr><td colSpan={6} className="px-4 py-10 text-center text-[#63534E]">No redemptions yet.</td></tr>}
          {items.map((r) => (
            <tr key={r.id} className="border-t border-[#D4AF37]/20">
              <td className="px-4 py-3">{r.user_name}<div className="text-xs font-mono text-[#63534E]">{r.user_email}</div></td>
              <td className="px-4 py-3 capitalize">{r.kind}</td>
              <td className="px-4 py-3 font-mono">${r.amount.toFixed(2)}</td>
              <td className="px-4 py-3 text-xs max-w-[240px]">{r.kind === "cash" ? `${r.payout_method?.toUpperCase()}: ${r.payout_details}` : r.shipping_address || r.seva_key}</td>
              <td className="px-4 py-3"><span className={`px-2 py-0.5 rounded-full text-xs ${pill(r.status)}`}>{r.status}</span></td>
              <td className="px-4 py-3">{r.status === "pending" && <div className="flex gap-2">
                <Btn data-testid={`redeem-approve-${r.id}`} onClick={() => set(r.id, "completed")}>{r.kind === "cash" ? "Mark paid" : "Mark shipped"}</Btn>
                <Btn data-testid={`redeem-reject-${r.id}`} className="bg-red-700 hover:bg-red-800" onClick={() => set(r.id, "rejected")}>Reject &amp; refund</Btn>
              </div>}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

const EXPORTS = ["transactions", "suppliers", "ads", "redemptions", "teachers", "products"];

export function ExportAdminTab() {
  const dl = async (kind) => {
    try {
      const r = await api.get(`/admin/export/${kind}`, { responseType: "blob" });
      const a = document.createElement("a");
      a.href = URL.createObjectURL(r.data);
      a.download = `pancham-dham-${kind}-${new Date().toISOString().slice(0, 10)}.csv`;
      a.click();
    } catch (e) { toast.error(formatError(e)); }
  };
  return (
    <div data-testid="export-admin-tab" className="bg-[#FFFDF9] sacred-border rounded-2xl p-7">
      <h3 className="font-serif text-2xl text-[#4A0E17]">Download data for your accountant</h3>
      <p className="text-sm text-[#63534E] mt-2">Each file is a CSV that opens in Excel, Google Sheets or Tally.</p>
      <div className="mt-6 grid sm:grid-cols-3 gap-3">
        {EXPORTS.map((k) => (
          <button key={k} data-testid={`export-${k}`} onClick={() => dl(k)} className="flex items-center justify-between px-5 h-14 rounded-xl border border-[#D4AF37]/40 bg-white hover:bg-[#F3E5AB]/40 transition-sacred capitalize text-[#4A0E17] font-semibold">
            {k} <Download className="w-4 h-4 text-[#E65100]" />
          </button>
        ))}
      </div>
    </div>
  );
}

export { fileUrl };
